import React, { useState, useRef } from 'react';
import { Bot, FileText, Users, Shield, ArrowRight, MessageCircle } from 'lucide-react';
import {IconBook, IconBookmarks} from '@tabler/icons-react';
import ChatbotModal from './ChatbotModal'; // Import the chatbot modal

const IsEmployee = localStorage.getItem('userRole')==='EMPLOYEE';

const PolicyTypesComponent = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [selectedPolicyType, setSelectedPolicyType] = useState(null);
  
  let policies = [
    {
      id: 'office-policies',
      title: 'Office Policies',
      icon: IconBook,
      iconColor: '#1e88e5',
      description: 'Comprehensive guidelines that govern daily workplace operations and employee conduct.',
      details: [
        'Workplace conduct and behavior standards',
        'Office hours and attendance requirements',
        'Dress code and professional appearance',
        'Technology usage and security protocols',
        'Facility usage and safety procedures'
      ],
      color: 'primary',
      gradient: 'linear-gradient(135deg, #2196f3 0%, #1e88e5 100%)'
    }
  ];

  if (!IsEmployee) {
    policies.push({ 
      id: 'hr-handbook',
      title: 'HR Handbook',
      icon: IconBookmarks,
      iconColor: '#3d52a0',
      description: 'Essential processes and procedures for HR professionals to manage employee lifecycle effectively.',
      details: [
        'Employee onboarding procedures and checklists',
        'Offboarding and exit interview processes',
        'Performance management and review cycles',
        'Leave management and approval workflows',
        'Disciplinary actions and conflict resolution'
      ],
      color: 'secondary',
      gradient: 'linear-gradient(135deg, #3d52a0 0%, #7091e6 100%)'
    });
  }
    
  const handleChatbotClick = (policyId) => {
    setSelectedPolicyType(policyId);
    setChatbotOpen(true);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      minWidth: '100%',
    //   background: '#f8fafc',
      padding: '0 16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    maxWidth: {
      maxWidth: '100%',
      margin: '0 auto'
    },
    header: {
      textAlign: 'center',
      marginBottom: '64px'
    },
    headerIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '64px',
      height: '64px',
      background: 'linear-gradient(135deg, #2196f3 0%, #3d52a0 100%)',
      borderRadius: '50%',
      marginBottom: '24px'
    },
    headerTitle: {
      fontSize: '2.5rem',
      fontWeight: 'bold',
      color: '#121926',
      marginBottom: '16px',
      margin: 0
    },
    headerDescription: {
      fontSize: '1.25rem',
      color: '#697586',
      maxWidth: '600px',
      margin: '0 auto',
      lineHeight: '1.6'
    },
    cardsGrid: {
      display: 'grid',
      gridTemplateColumns: `repeat(${IsEmployee ? 1 : 2}, 1fr)`, // Always 2 cards per row
      gap: '32px',
    //   marginBottom: '48px'
    },

    card: {
      width: '100%',
      position: 'relative',
      background: 'var(--card-bg, white)',
      borderRadius: '16px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
      animation: 'fadeInUp 0.8s ease-out forwards'
    },
    cardHovered: {
      transform: 'translateY(-8px) scale(1.02)',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
      borderColor: '#2196f3'
    },
    cardHeader: {
      height: '8px'
    },
    cardContent: {
      padding: '32px'
    },
    cardTitleSection: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '24px'
    },
    cardIconContainer: {
      padding: '8px',
      //borderRadius: '12px',
      //boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    },
    cardTitleText: {
      marginLeft: '16px',
      fontSize: '1.05rem',
      fontWeight: 'bold',
      color: '#121926',
      marginLeft: '10px'
    },
    cardDescription: {
      color: '#697586',
      marginBottom: '24px',
      fontSize: '1rem',
      lineHeight: '1.7'
    },
    detailsSection: {
      marginBottom: '32px'
    },
    detailsTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#364152',
      marginBottom: '16px'
    },
    detailsList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    detailItem: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: '12px',
      transition: 'all 0.3s ease'
    },
    detailBullet: {
      width: '8px',
      height: '8px',
      background: '#2196f3',
      borderRadius: '50%',
      marginTop: '12px',
      marginRight: '12px',
      flexShrink: 0
    },
    detailText: {
      color: '#4b5565',
      lineHeight: '1.6'
    },
    chatbotButton: {
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
      color: '#ffffff',
      fontWeight: '600',
      padding: '16px 24px',
      borderRadius: '12px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontSize: '1rem',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    },
    chatbotButtonHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)'
    },
    chatbotButtonContent: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px'
    },
    chatbotButtonOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(255, 255, 255, 0.2)',
      opacity: 0,
      transition: 'opacity 0.3s ease'
    },
    floatingIcon: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      opacity: 0.1,
      pointerEvents: 'none'
    },
    bottomCta: {
      textAlign: 'center',
      background: 'var(--card-bg, white)',
      borderRadius: '16px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
      padding: '32px'
    },
    bottomCtaTitle: {
      fontSize: '1.75rem',
      fontWeight: 'bold',
      color: '#121926',
      marginBottom: '16px',
      margin: 0
    },
    bottomCtaDescription: {
      color: '#697586',
      marginBottom: '24px',
      fontSize: '1.125rem',
      lineHeight: '1.6'
    },
    ctaButtons: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      alignItems: 'center'
    },
    ctaButtonPrimary: {
      background: 'linear-gradient(135deg, #2196f3 0%, #3d52a0 100%)',
      color: '#ffffff',
      padding: '12px 32px',
      borderRadius: '12px',
      border: 'none',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontSize: '1rem',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    },
    ctaButtonSecondary: {
      background: 'transparent',
      color: '#2196f3',
      border: '2px solid #2196f3',
      padding: '12px 32px',
      borderRadius: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontSize: '1rem'
    },
    buttonContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    '@media (min-width: 640px)': {
      ctaButtons: {
        flexDirection: 'row',
        justifyContent: 'center'
      }
    }
  };

  const overlayRef = useRef();


  return (
    <>
      <div style={styles.container}>
        <div style={styles.maxWidth}>
          {/* Policy Cards */}
          <div style={styles.cardsGrid}>
            {policies.map((policy, index) => {
              const IconComponent = policy.icon;
              const isHovered = hoveredCard === policy.id;
              
              return (
                <div
                  key={policy.id}
                  style={{
                    ...styles.card,
                    ...(isHovered ? styles.cardHovered : {}),
                    animationDelay: `${index * 200}ms`
                  }}
                  onMouseEnter={() => setHoveredCard(policy.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  {/* Gradient Header */}
                  <div style={{
                    ...styles.cardHeader,
                    background: policy.gradient
                  }} />
                  
                  {/* Card Content */}
                  <div style={styles.cardContent}>
                    {/* Icon and Title */}
                    <div style={styles.cardTitleSection}>
                      <div style={styles.cardIconContainer}>
                           <IconComponent size={24} color={policy.iconColor} />
                      </div>
                      <h2 style={styles.cardTitleText}>
                         {policy.title}
                      </h2>
                  </div>

                    {/* Description */}
                    <p style={styles.cardDescription}>
                      {policy.description}
                    </p>

                    {/* Details List */}
                    <div style={styles.detailsSection}>
                      <h3 style={styles.detailsTitle}>
                        What's Included:
                      </h3>
                      <ul style={styles.detailsList}>
                        {policy.details.map((detail, detailIndex) => (
                          <li 
                            key={detailIndex}
                            style={styles.detailItem}
                          >
                            <div style={styles.detailBullet} />
                            <span style={styles.detailText}>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Chatbot Button - Updated onClick */}
                    <button
                      onClick={() => handleChatbotClick(policy.id)}
                      style={{
                        ...styles.chatbotButton,
                        background: policy.gradient,
                        ...(isHovered ? styles.chatbotButtonHover : {})
                      }}
                      onMouseEnter={() => {
                          if (overlayRef.current) overlayRef.current.style.opacity = '1';
                      }}
                      onMouseLeave={() => {
                          if (overlayRef.current) overlayRef.current.style.opacity = '0';
                      }}
                    >
                      <div ref={overlayRef} style={styles.chatbotButtonOverlay} />
                      <div style={styles.chatbotButtonContent}>
                        <Bot size={20} />
                        <span>Ask AI Assistant</span>
                        <ArrowRight size={20} />
                      </div>
                    </button>
                  </div>

                  {/* Floating Animation Elements */}
                  <div style={styles.floatingIcon}>
                    <MessageCircle size={48} color="#cdd5df" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Chatbot Modal */}
      <ChatbotModal 
        isOpen={chatbotOpen}
        onClose={() => setChatbotOpen(false)}
        policyType={selectedPolicyType}
      />
    </>
  );
};

export default PolicyTypesComponent;